
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { CaretRight, CheckCircle, ClipboardText, SealCheck } from '@phosphor-icons/react';
import { DashboardWidget, WidgetKopf, WidgetLaedt, WidgetLeer } from './DashboardWidget';

interface MeinePruefungenWidgetProps {
  userProfile?: any;
  onNavigate?: (path: string) => void;
  onNavigateToRechnung?: (id: string) => void;
}

type Art = 'pruefung' | 'freigabe';

interface OffeneAufgabe {
  id: string;
  rechnungsnummer: string | null;
  unternehmen: string | null;
  bezeichnung: string | null;
  brutto: number | null;
  faelligkeitsdatum: string | null;
  art: Art;
}

const formatEuro = (v: number | null) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const MeinePruefungenWidget: React.FC<MeinePruefungenWidgetProps> = ({ userProfile, onNavigate, onNavigateToRechnung }) => {
  const [aufgaben, setAufgaben] = useState<OffeneAufgabe[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userProfile?.id) fetchData();
    else setLoading(false);
  }, [userProfile?.id]);

  const fetchData = async () => {
    try {
      // Alle nicht bezahlten Rechnungen, die mir zur Prüfung ODER Freigabe zugeordnet sind
      const { data } = await supabase
        .from('rechnungen')
        .select('id, rechnungsnummer, unternehmen, bezeichnung, brutto, faelligkeitsdatum, status, tech_pruefung_an, tech_pruefung_status, freigabe_angefordert_an, freigegeben')
        .or(`tech_pruefung_an.eq.${userProfile.id},freigabe_angefordert_an.eq.${userProfile.id}`)
        .neq('status', 'bezahlt')
        .is('archiviert_am', null);

      const result: OffeneAufgabe[] = [];
      (data || []).forEach((r: any) => {
        // Sachliche Prüfung liegt mir vor
        if (r.tech_pruefung_an === userProfile.id && r.tech_pruefung_status === 'angefordert') {
          result.push({ ...r, art: 'pruefung' });
        }
        // Vier-Augen-Freigabe liegt mir vor (noch nicht freigegeben)
        else if (r.freigabe_angefordert_an === userProfile.id && !r.freigegeben) {
          result.push({ ...r, art: 'freigabe' });
        }
      });

      // Fällige zuerst
      result.sort((a, b) => {
        if (!a.faelligkeitsdatum) return 1;
        if (!b.faelligkeitsdatum) return -1;
        return new Date(a.faelligkeitsdatum).getTime() - new Date(b.faelligkeitsdatum).getTime();
      });

      setAufgaben(result);
    } catch (err) {
      console.error('Error fetching Prüfungen:', err);
    } finally {
      setLoading(false);
    }
  };

  const zurListe = () => onNavigate?.('/admin/rechnungen');

  return (
    <DashboardWidget farbe="--dw-tuerkis" icon={SealCheck} ariaLabel="Meine Prüfungen" stapel>
      <button type="button" onClick={zurListe} data-tip="Meine Prüfungen – Rechnungen, die Ihnen zur sachlichen Prüfung oder Freigabe vorliegen"
        className="block w-full border-0 bg-transparent p-0 text-left" style={{ fontFamily: 'inherit', cursor: 'pointer' }}>
        <WidgetKopf farbe="--dw-tuerkis" icon={ClipboardText} titel="Meine Prüfungen" untertitel="Rechnungen liegen mir vor"
          rechts={!loading && aufgaben.length > 0 ? (
            <span className="flex shrink-0 items-center justify-center tabular-nums"
              style={{ minWidth: 26, height: 26, padding: '0 8px', borderRadius: 13, fontSize: 12.5, fontWeight: 800, background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>
              {aufgaben.length}
            </span>
          ) : undefined} />
      </button>

      {loading ? (
        <WidgetLaedt />
      ) : aufgaben.length > 0 ? (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {aufgaben.slice(0, 5).map(a => (
            <button type="button" key={`${a.art}-${a.id}`} data-tip="Rechnung öffnen – Zur Detailseite der Rechnung"
              onClick={() => onNavigateToRechnung ? onNavigateToRechnung(a.id) : zurListe()}
              className="dw-zeile-fill dw-zeile flex w-full items-center border-0 text-left"
              style={{ gap: 10, padding: '8px 10px', borderRadius: 14, fontFamily: 'inherit', cursor: 'pointer' }}>
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{a.unternehmen || a.bezeichnung || 'Ohne Bezeichnung'}</span>
                <span className="flex min-w-0 items-center" style={{ gap: 6, fontSize: 12, color: 'var(--ink3)' }}>
                  <span className="flex shrink-0 items-center" style={{ height: 18, padding: '0 7px', borderRadius: 9, fontSize: 10.5, fontWeight: 800,
                    background: a.art === 'pruefung' ? 'var(--info-soft)' : 'var(--acc-soft)', color: a.art === 'pruefung' ? 'var(--info)' : 'var(--acc)' }}>
                    {a.art === 'pruefung' ? 'Prüfung' : 'Freigabe'}
                  </span>
                  <span className="truncate">
                    {a.rechnungsnummer || '—'}
                    {a.faelligkeitsdatum && ` · fällig ${new Date(a.faelligkeitsdatum).toLocaleDateString('de-DE')}`}
                  </span>
                </span>
              </span>
              <span className="shrink-0 whitespace-nowrap tabular-nums" style={{ fontSize: 13.5, fontWeight: 800 }}>{formatEuro(a.brutto)}</span>
              <CaretRight weight="bold" size={12} className="shrink-0" style={{ color: 'var(--ink3)' }} />
            </button>
          ))}
          {aufgaben.length > 5 && (
            <button type="button" onClick={zurListe} className="self-center border-0 bg-transparent"
              style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 700, color: 'var(--acc)', fontFamily: 'inherit', cursor: 'pointer' }}>
              +{aufgaben.length - 5} weitere anzeigen
            </button>
          )}
        </div>
      ) : (
        <WidgetLeer icon={CheckCircle} text="Nichts zu prüfen" farbeIcon="var(--ok)" />
      )}
    </DashboardWidget>
  );
};

export default MeinePruefungenWidget;
